import React from 'react';
import { useSettings, ChordNotationType } from '../../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';

const SettingsPage: React.FC = () => {
  const {
    isDarkMode,
    chordNotationType,
    isAutoLevelUpEnabled,
    isSoundEnabled,
    toggleDarkMode,
    setChordNotationType,
    setAutoLevelUpEnabled,
    setSoundEnabled
  } = useSettings();
  const navigate = useNavigate();

  const handleChordNotationChange = (type: ChordNotationType) => {
    setChordNotationType(type);
  };

  const toggleAutoLevelUp = () => {
    setAutoLevelUpEnabled(!isAutoLevelUpEnabled);
  };

  const toggleSound = () => {
    setSoundEnabled(!isSoundEnabled);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Settings
            </h1>
            <button
              onClick={() => navigate(-1)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Back
            </button>
          </div>

          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6`}>
            {/* Theme Setting */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Appearance
              </h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Dark Mode
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Toggle between light and dark theme
                  </p>
                </div>
                <button
                  onClick={toggleDarkMode}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                      isDarkMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Audio Settings */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Audio
              </h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Enable Sound
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Play sounds through browser. Disable to use external DAW or digital piano speakers.
                  </p>
                </div>
                <button
                  onClick={toggleSound}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isSoundEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                      isSoundEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Gameplay Settings */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Gameplay
              </h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Auto Level Up
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Automatically advance to the next level when reaching maximum experience
                  </p>
                </div>
                <button
                  onClick={toggleAutoLevelUp}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isAutoLevelUpEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                      isAutoLevelUpEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Chord Notation Setting */}
            <div>
              <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Chord Notation
              </h2>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    id="full-chord-notation"
                    name="chordNotation"
                    type="radio"
                    checked={chordNotationType === 'full'}
                    onChange={() => handleChordNotationChange('full')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="full-chord-notation" className={`ml-3 block ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="font-medium">Full Names (Cmin, Caug, Cmaj<sup>7</sup>)</div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Complete chord quality names
                    </div>
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="symbol-chord-notation"
                    name="chordNotation"
                    type="radio"
                    checked={chordNotationType === 'symbols'}
                    onChange={() => handleChordNotationChange('symbols')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="symbol-chord-notation" className={`ml-3 block ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="font-medium">Symbols (C-, C+, CΔ<sup>7</sup>)</div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Abbreviated chord symbols
                    </div>
                  </label>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
